import { stringify } from 'query-string';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { useRouter } from '@/hooks/useRouter';
import { getUserLocale } from '@/redux/selectors/auth/user';
import { getUserCurrency } from '@/redux/selectors/currency';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { EListingSource } from '@/services/analytics/listings/types';
import apiRequest from '@/services/apiRequest';
import { IData as IRentalData, IMeta as IRentalMeta } from '@/services/types/search/rentals/id';
import { getSearchApi } from '@/utility/getCoreApi';
import { getRideBaseUrl } from '@/utility/getRideBaseUrl';
import { mapRentalsToTiles } from '@/utility/mapSearchResultToTile';

import { ETagSlug } from './Tags';

type THomePageRecommendationsResponse = {
  data: IRentalData[];
  meta?: IRentalMeta;
};

export enum ERecommendationsMode {
  RENTALS = 'rentals',
  DELIVERY = 'delivery',
}

const EXPECTED_RENTALS = 12;

const RIDE_UTM_PARAMS = 'utm_source=outdoorsy&utm_medium=homepage&utm_campaign=recommendations';

export const useRecommendations = (
  tag: ETagSlug,
  listingSource?: EListingSource,
  mode: ERecommendationsMode = ERecommendationsMode.RENTALS,
) => {
  const router = useRouter();

  const userFavorites = useSelector(getUserFavorites);

  // For testing purposes allow to pass the address as a query param, and use
  // it as the default address instead of the user's location based on the IP.
  const address = router.query.address as string;
  const { data: locationRecommendations } = useRecommendationsData(
    !router.isReady,
    tag,
    address,
    mode,
  );
  const { data: fallbackRecommendations } = useRecommendationsData(
    // Skip the request if the location recommendations still loading, or we got less rentals than expected.
    !locationRecommendations?.data || locationRecommendations.data.length === EXPECTED_RENTALS,
    tag,
    'USA',
    mode,
  );

  return useMemo(() => {
    // Return the loading tiles if the location recommendations are still loading, or we got
    // less rentals than expected and the fallback recommendations are still loading.
    if (
      !locationRecommendations?.data ||
      (locationRecommendations.data.length < EXPECTED_RENTALS && !fallbackRecommendations?.data)
    ) {
      return {
        loading: true,
        tiles: Array.from({ length: EXPECTED_RENTALS }, () => ({ loading: true })),
      };
    }

    return {
      loading: false,
      location:
        // Show the location if we got rentals only from the location recommendations.
        locationRecommendations.data.length >= EXPECTED_RENTALS &&
        locationRecommendations.meta?.city &&
        locationRecommendations.meta?.state
          ? `${locationRecommendations.meta.city}, ${locationRecommendations.meta.state}`
          : undefined,
      tiles: mapRentalsToTiles({
        rentals:
          // If we don't have enough rentals from the location recommendations, show the fallback recommendations.
          locationRecommendations.data.length < EXPECTED_RENTALS
            ? fallbackRecommendations?.data || []
            : locationRecommendations.data,
        favorites: userFavorites,
        queryParams: router.query,
        hasImageCarousel: true,
        listingSource,
      }).map(tile =>
        // Car listings live on ride.auto — their slug is already in ride.auto's
        // /car-rental/... format, so point the tile there instead of a local route.
        tag === ETagSlug.Cars && tile.slug
          ? { ...tile, linkUrl: `${getRideBaseUrl()}${tile.slug}?${RIDE_UTM_PARAMS}` }
          : tile,
      ),
    };
  }, [
    locationRecommendations?.data,
    locationRecommendations?.meta,
    fallbackRecommendations?.data,
    userFavorites,
    router.query,
    listingSource,
    tag,
  ]);
};

const useRecommendationsData = (
  skip: boolean,
  tag: ETagSlug,
  address = '',
  mode: ERecommendationsMode,
) => {
  const currency = useSelector(getUserCurrency);
  const locale = useSelector(getUserLocale);

  // "Cars" isn't a homepage-recommendations tag — it swaps the data source to the
  // main search endpoint filtered to Ride's auto inventory (same index, same shape).
  const url =
    tag === ETagSlug.Cars
      ? `${getSearchApi()}/rentals?${stringify({
          raw_json: true,
          mini: 'true',
          address,
          currency,
          locale,
          'page[limit]': EXPECTED_RENTALS,
          'filter[rental_category]': 'auto',
        })}`
      : `${getSearchApi()}/rentals/homepage-recommendations?${stringify({
          raw_json: true,
          tag_slug: tag,
          mini: 'true',
          address,
          currency,
          locale,
          mode,
        })}`;

  return useSWR<THomePageRecommendationsResponse>(
    skip ? null : url,
    (requestUrl: string) => apiRequest({ url: requestUrl }),
    {
      revalidateIfStale: false, // Avoid fetching from the stale cache
      revalidateOnMount: true, // Always fetch fresh data on mount
    },
  );
};
